import { useState } from 'react'

import classNames from 'classnames'
import { Field, Form, Formik, FormikProps, FormikValues } from 'formik'
import {
  AddAuthorizationsAndInvites,
  AddAuthorizationsAndInvitesVariables,
  FetchElementAuthorizations_knownUsers,
  CurrentUserEmailAddresses_user_emails,
} from 'globalTypes'
import { filter, startsWith, toLower } from 'lodash'
import Autosuggest from 'react-autosuggest'
import TagsInput from 'react-tagsinput'

import { useMutation } from '@apollo/client'
import { Trans, msg } from '@lingui/macro'
import { useLingui } from '@lingui/react'
import Button from 'Root/App/Form/Button'
import { MAX_TAGS } from 'lib/constants'

import styles from '../add-form.module.css'
import Suggestion from './Suggestion'
import Tag from './Tag'
import MUTATION from './add-authorizations-and-invites.graphql'
import autosuggest from './autosuggest.module.css'
import { submit, validationSchema } from './form'

interface Props {
  id: string
  knownUsers: FetchElementAuthorizations_knownUsers[]
  creator: {
    id: string
    email: string
    __typename: string
    bio: string
    name: string
    picture: string
    website: string
  }
  currentUserData: CurrentUserEmailAddresses_user_emails[]
}

const AddUser = ({ id, knownUsers, creator, currentUserData }: Props) => {
  const { _ } = useLingui()
  const [shareEmailError, setShareEmailError] = useState<string | null>(null)
  const [shareInvalidEmailWarning, setshareInvalidEmailWarning] = useState<
    string | null
  >(null)

  const [mutate] = useMutation<
    AddAuthorizationsAndInvites,
    AddAuthorizationsAndInvitesVariables
  >(MUTATION, {
    refetchQueries: ['GetElementAuthorizationForUsers'],
  })

  function renderInput({ addTag, ...props }) {
    const handleOnChange = (e, { method }) => {
      if (method === 'enter') {
        e.preventDefault()
      } else {
        props.onChange(e)
      }
    }

    const suggestions = filter(
      knownUsers || [],
      ({ name, email }) =>
        startsWith(toLower(name), toLower(props.value)) ||
        startsWith(toLower(email), toLower(props.value))
    )

    const shouldRenderSuggestions = (value) =>
      Boolean(value) && value.trim().length > 0

    return (
      <Autosuggest
        getSuggestionValue={(suggestion) => suggestion.email}
        inputProps={{ ...props, onChange: handleOnChange }}
        onSuggestionsClearRequested={() => {}}
        onSuggestionSelected={(_e, { suggestion }) => addTag(suggestion.email)}
        onSuggestionsFetchRequested={() => {}}
        ref={props.ref}
        renderSuggestion={Suggestion}
        shouldRenderSuggestions={shouldRenderSuggestions}
        suggestions={suggestions}
        theme={autosuggest}
      />
    )
  }

  const handleSubmit = async (values, formikHelpers) => {
    setShareEmailError(null)
    setshareInvalidEmailWarning(null)
    const fn = submit(mutate, id, currentUserData, creator.email)

    return await fn(values, formikHelpers)
  }

  return (
    <Formik
      initialValues={{ emails: [] }}
      enableReinitialize
      onSubmit={handleSubmit}
      validationSchema={validationSchema}
      validateOnBlur={false}
    >
      {(formikProps: FormikProps<FormikValues>) => (
        <Form className={styles.form}>
          <Field name="emails">
            {({ field, form }) => {
              const { name, value } = field
              const { setFieldValue, setFieldTouched } = form
              const maxTagsReached = value.length === MAX_TAGS

              const EMAIL_REGEX = /^(.+\@.+\..+)$/i

              const onChange = (value) => {
                if (value.length <= MAX_TAGS) {
                  setShareEmailError(null)

                  const cleanedEmails = value.map((v) =>
                    v.trim().replace(/[;,]+$/, '')
                  )

                  // combine consecutive non-email tokens into a single tag
                  // so pasted or space-separated text that isn't an email
                  // is shown as one tag instead of many
                  const collapsed: string[] = []
                  let buffer: string[] = []

                  const pushBuffer = () => {
                    if (buffer.length > 0) {
                      collapsed.push(buffer.join(' '))
                      buffer = []
                    }
                  }

                  cleanedEmails.forEach((e) => {
                    if (EMAIL_REGEX.test(e)) {
                      // flush any accumulated non-email parts
                      pushBuffer()
                      collapsed.push(e)
                    } else if (e) {
                      // accumulate non-email parts
                      buffer.push(e)
                    }
                  })
                  pushBuffer()

                  // detect invalid emails to show a warning if any non-email parts exist
                  const hasNonEmail = collapsed.some(
                    (t) => !EMAIL_REGEX.test(t)
                  )
                  if (hasNonEmail) {
                    setshareInvalidEmailWarning(
                      _(
                        msg`Oops, le email evidenziate non sono valide e verranno escluse.`
                      )
                    )
                  } else {
                    setshareInvalidEmailWarning(null)
                  }

                  setFieldValue(name, collapsed)
                  setFieldTouched(name, true)
                } else {
                  setShareEmailError(
                    _(msg`Oops, puoi inserire max ${MAX_TAGS} email per volta.`)
                  )
                }
              }

              const renderTag = ({ key, ...props }) => {
                const tagValue = props.tag
                const display = props.getTagDisplayValue
                  ? props.getTagDisplayValue(tagValue)
                  : tagValue
                const isValid = EMAIL_REGEX.test(display)
                return (
                  <Tag
                    {...props}
                    index={key}
                    key={key}
                    creator={creator}
                    isValid={isValid}
                  />
                )
              }

              return (
                <div className="flex flex-col gap-1 mb-4">
                  <TagsInput
                    addKeys={[9, 13, 32, 59, 108, 188, 222]}
                    addOnBlur
                    addOnPaste
                    className={styles.tagsInput}
                    inputProps={{
                      placeholder: !maxTagsReached
                        ? _(msg`Invita o aggiungi indirizzi email...`)
                        : '',
                      disabled: maxTagsReached,
                    }}
                    onChange={onChange}
                    onlyUnique
                    pasteSplit={(data) =>
                      data
                        .split(/[;,\s]+/)
                        .map((e) => e.trim())
                        .filter((e) => e)
                    }
                    renderInput={renderInput}
                    renderTag={renderTag}
                    value={value}
                  />
                  <div className="flex items-center w-full">
                    {shareEmailError && (
                      <span className="text-red-500 text-left text-sm">
                        {shareEmailError}
                      </span>
                    )}
                    {shareInvalidEmailWarning && (
                      <span className="text-red-500 text-left text-sm">
                        {shareInvalidEmailWarning}
                      </span>
                    )}

                    <label
                      className={classNames([
                        'text-right text-xs ml-auto',
                        {
                          'text-red-500': maxTagsReached,
                          'text-gray-500': !maxTagsReached,
                        },
                      ])}
                    >
                      {value?.length ?? 0}/{MAX_TAGS}
                    </label>
                  </div>
                </div>
              )
            }}
          </Field>

          <Button
            disabled={!formikProps.dirty || !formikProps.isValid}
            processing={formikProps.isSubmitting}
            size="XS"
            type="submit"
          >
            <Trans>Condividi</Trans>
          </Button>
        </Form>
      )}
    </Formik>
  )
}

export default AddUser
